/**
 * Which email address the current session belongs to, for display only (walkthrough F-38).
 *
 * The server keeps no address on the session (the account directory deliberately never persists
 * one), so the browser remembers the address that was accepted at sign-in, bound to the org and
 * person the server returned for it. It is shown only while the live session is that same org and
 * person: a different account's session never shows this address. Sign-out and a lost session
 * forget it. Nothing here is a credential or decides anything; the cookie is still the identity.
 */

import { useEffect, useReducer } from 'react';

interface RememberedAccount {
  org_id: string;
  principal_id: string;
  email: string;
}

interface SessionIdentity {
  org_id: string;
  principal_id: string;
}

/** Exported so another tab's sign-in or sign-out (a `storage` event on this key) can re-read the session. */
export const SIGNED_IN_ACCOUNT_KEY = 'auteur.signedInAccount';
const STORAGE_KEY = SIGNED_IN_ACCOUNT_KEY;

function storage(): Storage | null {
  try {
    return typeof window === 'undefined' ? null : window.localStorage;
  } catch {
    return null;
  }
}

export function rememberSignedInAccount(session: SessionIdentity, email: string): void {
  const address = email.trim().toLowerCase();
  if (!address || !session.org_id || !session.principal_id) return;
  const record: RememberedAccount = { org_id: session.org_id, principal_id: session.principal_id, email: address };
  try {
    storage()?.setItem(STORAGE_KEY, JSON.stringify(record));
  } catch {
    // Storage can be full or blocked; the menu then shows the account without its address.
  }
}

/** The remembered address, only when it belongs to exactly this session's org and person. */
export function signedInEmail(session: SessionIdentity | null | undefined): string | null {
  if (!session) return null;
  try {
    const raw = storage()?.getItem(STORAGE_KEY);
    if (!raw) return null;
    const record = JSON.parse(raw) as Partial<RememberedAccount>;
    if (record.org_id !== session.org_id || record.principal_id !== session.principal_id) return null;
    return typeof record.email === 'string' && record.email ? record.email : null;
  } catch {
    return null;
  }
}

export function forgetSignedInAccount(): void {
  try {
    storage()?.removeItem(STORAGE_KEY);
  } catch {
    // Nothing to forget when storage is unavailable.
  }
}

/**
 * The remembered address for this session, re-read at once when another tab signs in or out, so a
 * stale address is hidden even before (or if) the session re-check answers.
 */
export function useSignedInEmail(session: SessionIdentity | null | undefined): string | null {
  const [, rerender] = useReducer((count: number) => count + 1, 0);
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const onStorage = (event: StorageEvent) => {
      if (event.key === null || event.key === SIGNED_IN_ACCOUNT_KEY) rerender();
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);
  return signedInEmail(session);
}
